import { useState, useMemo, useEffect } from "react";
// Import Lucide icons
import { 
    Brain, Image as ImageIcon, MapPin, CheckCircle, AlertCircle, 
    BarChart3, Sparkles, Quote, Eye, ListChecks, Target, Lightbulb, 
    TrendingUp, ThumbsUp, Users, MessageSquare, Camera, X,
    Copy, Check, User, Scale, Heart, TrendingDown, Clock, Info,
    ChevronLeft, ChevronRight, Palette, Tag, UserPlus, Zap, Activity 
} from "lucide-react";
import { motion } from 'framer-motion';

// --- Data Import ---
import { wardData } from '../../data/wardData.js';
import { getStorageURL } from "../../firebase.js";

// --- API Endpoint ---
const DAVINCI_ANALYSIS_API_URL = "https://davinci-image-frontend-service-84264019333.europe-west1.run.app";

// --- Channel Options ---
const CAMPAIGN_CHANNELS = [
    { id: 'social', name: 'Social' }, { id: 'leaflet', name: 'Print' },
    { id: 'email', name: 'Email' }, { id: 'website', name: 'Website' },
];

// Import Helpers from Helpers File
import {ImageDetailModal} from "./ImageModal/ImageModal.jsx"
import { ResultsContainer } from "./DavinciResultsContainer.jsx";
import { logCalls } from "../../logging.js";

// --- AESTHETIC HELPERS ---
const GlassContainer = ({ children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl overflow-hidden ${className}`}>
        {children}
    </div>
);

// --- Main Component ---
export const DavinciView = ({ 
    campaignId,
    allCampaignWards
}) => {
    const [selectedWards, setSelectedWards] = useState([]);
    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [highlightedArchetype, setHighlightedArchetype] = useState(null);

    const region = Object.values(allCampaignWards)[0];
    const authority = Object.values(region)[0];
    const wards = Object.values(authority);

    const handleWardToggle = (wardId) => {
        setSelectedWards(prev => 
            prev.includes(wardId) ? prev.filter(id => id !== wardId) : [...prev, wardId]
        );
    };

    const handleChannelToggle = (channelId) => {
        setSelectedChannels(prev => 
            prev.includes(channelId) ? prev.filter(id => id !== channelId) : [...prev, channelId]
        );
    };

    const {logVisionCall} = logCalls(campaignId)

    const runDavinciAnalysis = async () => {
        setIsLoading(true);
        setError(null);
        setResults(null);
        setHighlightedArchetype(null); 
        logVisionCall();
        try {
                const payload = {
                    selected_wards: selectedWards.length > 0 ? selectedWards : [], 
                    selected_mediums: selectedChannels.length > 0 ? selectedChannels : [] 
                };
                const endpointUrl = `${DAVINCI_ANALYSIS_API_URL}/analyze_campaign/${campaignId}`;
                
                console.hidden("Calling LIVE V5.9 API:", endpointUrl, "with payload:", JSON.stringify(payload));
                const response = await fetch(endpointUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || `API call failed with status ${response.status}`);
                }
                const apiResults = await response.json();
                console.hidden("V5.9 Analysis results received:", apiResults);
                setResults(apiResults);
        } catch (err) {
            console.error("Analysis failed:", err);
            setError(err.message || "An error occurred during analysis.");
        } finally {
            setIsLoading(false);
        }
    };

    // --- Dynamic content helpers ---
    const analysisScopeText = selectedWards.length === 0 ? `all ${wards?.length ?? 0} wards` : `${selectedWards.length} selected ${selectedWards.length === 1 ? 'ward' : 'wards'}`;
    const selectedChannelNames = selectedChannels.length === 0 ? ['All Channels'] : selectedChannels.map(id => CAMPAIGN_CHANNELS.find(c => c.id === id)?.name);
    const channelScopeText = selectedChannelNames.join(', ');

    // --- Logic for Modal Navigation ---
    const currentImageIndex = results?.ranked_meta_recommendations.findIndex(
        img => selectedImage && img.gcs_path === selectedImage.gcs_path
    ) ?? -1;
    const totalImages = results?.ranked_meta_recommendations.length ?? 0;
    const handleNextImage = () => {
        if (currentImageIndex > -1 && currentImageIndex < totalImages - 1) {
            setSelectedImage(results.ranked_meta_recommendations[currentImageIndex + 1]);
        }
    };
    const handlePrevImage = () => {
        if (currentImageIndex > 0) {
            setSelectedImage(results.ranked_meta_recommendations[currentImageIndex - 1]);
        }
    };

    return (
        <div className="p-8 space-y-8 min-h-screen animate-in fade-in duration-500">
            
            {/* Header (Simplified & Clean) */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-between items-end border-b border-slate-800/50 pb-6"
            >
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">
                        Image Strategist
                    </h1>
                    <p className="text-slate-400 text-sm font-medium">AI-Powered Visual Analysis and Image Strategy Engine</p>
                </div>
            </motion.div>

            {/* --- Input Panel --- */}
            <GlassContainer className="p-0">
                <div className="p-6 space-y-8">
                    
                    {/* Channel Selection */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="text-white" size={20} />
                                <h3 className="text-xs font-bold text-white uppercase tracking-widest">Target Channels</h3>
                            </div>
                            {selectedChannels.length > 0 && (
                                <button 
                                    onClick={() => setSelectedChannels([])}
                                    className="flex items-center text-xs font-bold text-slate-500 hover:text-white uppercase tracking-wider transition-colors"
                                >
                                    <X size={14} className="mr-1" /> Clear
                                </button>
                            )}
                        </div>
                        
                        <p className="text-sm text-slate-300 mb-4 font-medium leading-relaxed">Select one or more channels to target specific visual assets. Defaults to all if none are selected.</p>

                        <div className="flex flex-wrap gap-3">
                            {CAMPAIGN_CHANNELS.map((channel) => {
                                const isSelected = selectedChannels.includes(channel.id);
                                return (
                                    <button 
                                        key={channel.id} 
                                        onClick={() => handleChannelToggle(channel.id)}
                                        // UPDATED: Selection uses Dark Glass + Pure White Border/Glow (No Block White)
                                        className={`
                                            flex items-center px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-300 border group
                                            ${isSelected 
                                                ? 'bg-slate-900/80 text-white border-white/70 shadow-[0_0_15px_rgba(255,255,255,0.25)]' 
                                                : 'bg-slate-950/40 text-slate-400 border-slate-800 hover:border-slate-600 hover:text-slate-200 hover:bg-slate-900/60'
                                            }
                                        `}
                                    >
                                        {isSelected && <CheckCircle size={14} className="mr-2 text-white" />}
                                        {channel.name}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-4 text-xs text-slate-400 font-medium">
                            Optimising for: <span className="text-white font-bold">{channelScopeText}</span>
                        </div>
                    </div>

                    <div className="w-full h-px bg-slate-800/50"></div>

                    {/* Wards Selection */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <MapPin className="text-white" size={20} />
                                <h3 className="text-xs font-bold text-white uppercase tracking-widest">Analysis Scope (Optional)</h3>
                            </div>
                            {selectedWards.length > 0 && (
                                <button 
                                    onClick={() => setSelectedWards([])}
                                    className="flex items-center text-xs font-bold text-slate-500 hover:text-white uppercase tracking-wider transition-colors"
                                >
                                    <X size={14} className="mr-1" /> Clear
                                </button>
                            )}
                        </div>

                        <p className="text-sm text-slate-300 mb-4 font-medium leading-relaxed">Select specific wards to tailor the demographic analysis. Default is all campaign wards.</p>
                        
                        <div className="flex flex-wrap gap-3 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                            {wards && wards.length > 0 ? (
                                wards.map(ward => {
                                    const isSelected = selectedWards.includes(ward.ward_code);
                                    return (
                                        <button 
                                            key={ward.ward_code} 
                                            onClick={() => handleWardToggle(ward.ward_code)}
                                            // UPDATED: Selection uses Dark Glass + Pure White Border/Glow
                                            className={`
                                                flex items-center px-4 py-2 rounded-lg text-xs font-medium transition-all duration-300 border group truncate max-w-[220px]
                                                ${isSelected 
                                                    ? 'bg-slate-900/80 text-white border-white/70 shadow-[0_0_15px_rgba(255,255,255,0.25)]' 
                                                    : 'bg-slate-950/40 text-slate-400 border-slate-800 hover:border-slate-600 hover:text-slate-200 hover:bg-slate-900/60'
                                                }
                                            `}
                                        >
                                            {isSelected && <CheckCircle size={14} className="mr-2 text-white flex-shrink-0" />}
                                            <span className="truncate">{ward.ward_name}</span>
                                        </button>
                                    );
                                })
                            ) : ( 
                                <p className="text-slate-500 text-sm italic border border-dashed border-slate-800 p-4 rounded-lg w-full text-center">No wards available.</p> 
                            )}
                        </div>
                        <div className="mt-4 text-xs text-slate-400 font-medium">
                            Analysing based on: <span className="text-white font-bold">{analysisScopeText}</span>
                        </div>
                    </div>
                </div>

                {/* Action Footer */}
                <div className="px-6 py-4 bg-slate-950/30 border-t border-slate-800/50 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="text-sm text-slate-400 font-medium">
                        Ready to analyze campaign: <span className="text-white font-bold">{campaignId}</span>
                    </div>
                    <button 
                        onClick={runDavinciAnalysis} 
                        disabled={isLoading || !campaignId}
                        // UPDATED: Stealth Slate Button (Subtle until hover)
                        className={`
                            flex items-center gap-2 px-6 py-3 rounded-lg text-xs font-bold uppercase tracking-widest transition-all duration-300
                            ${isLoading || !campaignId
                                ? 'bg-slate-800/50 text-slate-600 cursor-not-allowed border border-slate-800'
                                : 'bg-slate-950 text-white hover:bg-slate-900 border border-slate-600 hover:border-white/50 hover:shadow-[0_0_20px_rgba(255,255,255,0.15)] shadow-lg'
                            }
                        `}
                    >
                        {isLoading ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.91l3-2.619z"></path>
                                </svg>
                                <span>Processing...</span>
                            </>
                        ) : (
                            <>
                                <Sparkles size={16} />
                                <span>Run Analysis</span>
                            </>
                        )}
                    </button>
                </div>
            </GlassContainer>

            {/* --- V5.9 Results Panel --- */}
            <GlassContainer className="min-h-[400px]">
                <div className="p-6 h-full">
                    {/* Initial State */}
                    {!results && !isLoading && !error && (
                        <div className="flex flex-col items-center justify-center h-64 text-slate-500 text-center">
                            <div className="p-4 bg-slate-950/50 rounded-full border border-slate-800 mb-4">
                                <ImageIcon size={48} className="text-slate-600" />
                            </div>
                            <h2 className="text-lg font-bold text-white uppercase tracking-widest mb-2">Image Strategy Awaits</h2>
                            <p className="text-sm text-slate-400 max-w-sm font-medium">Select channels and scope above, then run the analysis to generate your visual strategy.</p>
                        </div>
                    )}

                    {/* Loading State */}
                    {isLoading && (
                        <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                            <div className="relative mb-6">
                                <div className="absolute inset-0 bg-white/10 blur-xl rounded-full"></div>
                                <svg className="relative animate-spin h-12 w-12 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.91l3-2.619z"></path>
                                </svg>
                            </div>
                            <h2 className="text-lg font-bold text-white uppercase tracking-widest mb-2">Analyzing...</h2>
                            <p className="text-sm text-slate-400 font-medium">Applying V5.9 Dante Vision engine to your campaign...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="flex flex-col items-center justify-center h-64 text-center">
                            <div className="p-3 bg-rose-500/10 rounded-full border border-rose-500/20 mb-4">
                                <AlertCircle size={48} className="text-rose-400" />
                            </div>
                            <h2 className="text-lg font-bold text-rose-300 uppercase tracking-widest mb-2">Analysis Failed</h2>
                            <p className="text-sm font-mono bg-rose-950/30 text-rose-300/70 p-3 rounded border border-rose-900/50 max-w-lg break-words">
                                {error}
                            </p>
                        </div>
                    )}

                    {/* Results */}
                    {results && (
                        <ResultsContainer 
                            results={results} 
                            setSelectedImage={setSelectedImage} 
                            highlightedArchetype={highlightedArchetype} 
                            setHighlightedArchetype={setHighlightedArchetype}
                        />
                    )}
                </div>
            </GlassContainer>

            {/* Modal */}
            {selectedImage && (
                <ImageDetailModal 
                    image={selectedImage} 
                    onClose={() => setSelectedImage(null)}
                    onNext={handleNextImage}
                    onPrev={handlePrevImage}
                    isFirst={currentImageIndex === 0}
                    isLast={currentImageIndex === (totalImages - 1)}
                />
            )}
        </div>
    );
};